import '@react-sigma/core/lib/style.css';

// Import Sigma type and SigmaContainer
// Import Node programs
import Graph from 'graphology';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { GraphEntityTypeLabel } from '@/components/nodeGraph/utils/constants';
import { GraphEntityType } from '@/components/nodeGraph/utils/types';
// Added for warning icon
import { Button } from '@/components/ui/button';
import { useAsyncExports } from '@/hooks/useAsyncExports';
import { parseTechnologyVersion } from '@/sections/technology/utils';
// Import Loader
import { useGlobalState } from '@/state/global.state';
import { Asset, ResourceType, Risk, Seed, Technology } from '@/types';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';

/**
 * NodeDataTable - Selected Assets Data Display Component
 *
 * This component displays selected assets in a tabular format when users select
 * nodes in the NodeGraph. It provides a detailed view of selected assets with
 * their attributes and offers export functionality.
 *
 * Features:
 * - Asset Display: Shows DNS names, asset names, and connected attributes
 * - Interactive Rows: Click any asset to open detailed drawer view
 * - CSV Export: Download selected assets data as CSV file
 * - Attribute Extraction: Automatically extracts and displays connected attributes
 * - Responsive Layout: Fixed positioning with scrollable content
 * - Selection Count: Shows number of selected assets in header
 *
 * Data Processing:
 * - Filters selected nodes to only asset nodes (excludes attribute nodes)
 * - Extracts connected attributes from graph relationships
 * - Maps node IDs to actual asset objects
 * - Creates structured data for display and export
 *
 * UI Layout:
 * - Fixed positioning in bottom-right corner
 * - Maximum height of 60vh with scrollable content
 * - Sticky headers and first column
 * - Theme-aware styling with consistent design
 *
 * Export Functionality:
 * - Dynamic CSV headers based on available attributes
 * - Proper CSV formatting with quote escaping
 * - Timestamped filenames for organization
 * - Automatic browser download trigger
 */
const NodeDataTableComponent: FC<{
  selectedNodes: Set<string>;
  graph: Graph | null;
  assets: Asset[];
  seeds: Seed[];
  technologies: Technology[];
  risks: Risk[];
}> = ({ selectedNodes, graph, assets, technologies, seeds, risks }) => {
  const { drawerState } = useGlobalState();
  const [resourceType, setResourceType] = useState<GraphEntityType | null>(
    null
  );

  // Determine export type based on resource type
  const exportType = useMemo(() => {
    switch (resourceType) {
      case ResourceType.Asset:
        return 'asset' as const;
      case ResourceType.Risk:
        return 'risk' as const;
      case ResourceType.Technology:
        return 'technology' as const;
      case ResourceType.Seed:
        return 'seed' as const;
      default:
        return 'asset' as const;
    }
  }, [resourceType]);

  const { mutateAsync: exportData, isPending: isExporting } =
    useAsyncExports(exportType);

  useEffect(() => {
    if (selectedNodes.size === 0) return;
    const nodeId = Array.from(selectedNodes)[0];
    /* eslint-disable react-hooks/set-state-in-effect */
    if (nodeId.startsWith('#asset#')) setResourceType(ResourceType.Asset);
    if (nodeId.startsWith('#risk#')) setResourceType(ResourceType.Risk);
    if (nodeId.startsWith('#technology#'))
      setResourceType(ResourceType.Technology);
    if (nodeId.startsWith('#seed#')) setResourceType(ResourceType.Seed);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [selectedNodes]);

  const entityTypeLabel = useMemo(
    () => (resourceType ? GraphEntityTypeLabel[resourceType] : 'Node'),
    [resourceType]
  );

  const { assetsData, risksData, seedsData, technologiesData } = useMemo(() => {
    let assetsData: {
      key: string;
      dns: string;
      name: string;
      attributes: Record<string, string>;
      asset: Asset;
    }[] = [];
    let risksData: Risk[] = [];
    let technologiesData: Technology[] = [];
    let seedsData: Seed[] = [];

    if (!graph) {
      return {
        assetsData,
        risksData,
        technologiesData,
        seedsData,
      };
    }

    if (resourceType === ResourceType.Asset) {
      assetsData = Array.from(selectedNodes)
        .filter(nodeId => nodeId.startsWith('#asset#'))
        .map(nodeId => {
          const asset = assets.find(a => a.key === nodeId);
          if (!asset) return null;

          // Get connected attributes
          const attributes = new Map<string, string>();
          graph.forEachNeighbor(nodeId, neighborId => {
            if (neighborId.startsWith('#attribute#')) {
              const label = graph.getNodeAttribute(neighborId, 'label');
              const [key, ...valueParts] = label.split(': ');
              const value = valueParts.join(': '); // Rejoin in case value contains colons
              attributes.set(key, value);
            }
          });

          return {
            key: nodeId,
            dns: asset.dns,
            name: asset.name,
            attributes: Object.fromEntries(attributes),
            asset, // Keep the full asset object for the drawer
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);
    }
    if (resourceType === ResourceType.Risk) {
      risksData = Array.from(selectedNodes)
        .filter(nodeId => nodeId.startsWith('#risk#'))
        .map(nodeId => {
          const risk = risks.find(r => r.key === nodeId);
          if (!risk) return null;
          return risk;
        })
        .filter(Boolean) as Risk[];
    }
    if (resourceType === ResourceType.Technology) {
      technologiesData = Array.from(selectedNodes)
        .filter(nodeId => nodeId.startsWith('#technology#'))
        .map(nodeId => {
          const technology = technologies.find(r => r.key === nodeId);
          if (!technology) return null;
          return technology;
        })
        .filter(Boolean) as Technology[];
    }
    if (resourceType === ResourceType.Seed) {
      seedsData = Array.from(selectedNodes)
        .filter(nodeId => nodeId.startsWith('#seed#'))
        .map(nodeId => {
          const seed = seeds.find(r => r.key === nodeId);
          if (!seed) return null;
          return seed;
        })
        .filter(Boolean) as Seed[];
    }
    return {
      assetsData,
      risksData,
      technologiesData,
      seedsData,
    };
  }, [assets, risks, seeds, technologies, selectedNodes, graph, resourceType]);

  const handleAssetRowClick = useCallback(
    (asset: Asset) => {
      drawerState.handleOpenAssetDrawer(asset);
    },
    [drawerState]
  );

  const handleRiskRowClick = useCallback(
    (risk: Risk) => {
      drawerState.handleOpenVulnerabilityDrawer(risk);
    },
    [drawerState]
  );

  const handleTechnologyRowClick = useCallback(
    (technology: Technology) => {
      drawerState.handleOpenTechnologyDrawer(technology);
    },
    [drawerState]
  );

  const handleSeedRowClick = useCallback(
    (seed: Seed) => {
      drawerState.handleOpenSeedDrawer(seed);
    },
    [drawerState]
  );

  // Export using the real export API with toast notifications and auto-download
  const handleExportCSV = async () => {
    // Get all selected node keys
    const selectedKeys = Array.from(selectedNodes);

    // Call export API with selected keys - useAsyncExports handles:
    // - Job creation and polling
    // - Toast notifications with progress
    // - Download button in success toast
    // - Query invalidation to show in exports section
    await exportData({
      items: selectedKeys,
      format: 'csv',
      query: {
        node: { labels: ['Asset'] },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any, // Required by type but not used when items provided
    });
  };

  if (!graph || selectedNodes.size === 0) return null;

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary absolute top-4 right-4 z-20 flex max-h-[60vh] w-[400px] flex-col overflow-hidden rounded-lg border">
      <div className="border-chariot-stroke flex items-center justify-between border-b p-4">
        <h3 className="text-chariot-text-primary text-sm font-semibold">
          Selected {entityTypeLabel} (
          {assetsData.length +
            risksData.length +
            technologiesData.length +
            seedsData.length}
          )
        </h3>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          disabled={isExporting}
          className="rounded-full"
        >
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="scrollbar-thin scrollbar-track-chariot-background-secondary scrollbar-thumb-chariot-stroke h-full max-h-[calc(60vh-4rem)] overflow-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-30">
              <tr className="border-chariot-stroke border-b">
                <th className="bg-chariot-background-primary text-chariot-text-primary sticky left-0 z-40 w-[200px] px-4 py-2 text-left text-sm font-semibold">
                  {entityTypeLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {assetsData.map(item => (
                <Row
                  key={item.key}
                  title={item.dns}
                  description={item.dns !== item.name ? item.name : ''}
                  onClick={() => handleAssetRowClick(item.asset)}
                />
              ))}
              {risksData.map(item => (
                <Row
                  key={item.key}
                  title={item.name}
                  description={item.dns || ''}
                  onClick={() => handleRiskRowClick(item)}
                />
              ))}
              {technologiesData.map(item => (
                <Row
                  key={item.key}
                  title={getTechnologyDisplayProduct(item)}
                  description={parseTechnologyVersion(item.version, true)}
                  onClick={() => handleTechnologyRowClick(item)}
                />
              ))}
              {seedsData.map(item => (
                <Row
                  key={item.key}
                  title={item.identifier}
                  description={''}
                  onClick={() => handleSeedRowClick(item)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Memoized NodeDataTable to prevent re-renders when props haven't changed
 */
export const NodeDataTable = memo(
  NodeDataTableComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.selectedNodes.size === nextProps.selectedNodes.size &&
      prevProps.graph === nextProps.graph &&
      prevProps.assets.length === nextProps.assets.length &&
      prevProps.seeds.length === nextProps.seeds.length &&
      prevProps.technologies.length === nextProps.technologies.length &&
      prevProps.risks.length === nextProps.risks.length
    );
  }
);

const Row = memo(
  ({
    title,
    description,
    onClick,
  }: {
    title: string;
    description: string;
    onClick: () => void;
  }) => {
    return (
      <tr
        onClick={onClick}
        className="border-chariot-stroke cursor-pointer border-b transition-colors last:border-0"
      >
        <td className="bg-chariot-background-primary sticky left-0 z-20 w-[200px] px-4 py-2">
          <div className="text-chariot-brand-primary text-sm font-bold">
            {title}
          </div>
          {description && (
            <div className="text-chariot-text-primary truncate text-xs">
              {description}
            </div>
          )}
        </td>
      </tr>
    );
  }
);

Row.displayName = 'Row';
